import { fetchApi } from '@/services/api-client';

export const TIMETABLE_PATH = '/admin/timetable';

export type TimetableViewBy = 'class' | 'teacher' | 'room';

export type TimetableAssistant = {
  id: string;
  name: string;
};

export type TimetableViewCell = {
  weekday: number;
  periodId: string;
  /** This cell's real start/end time, resolved via its own class's lunchGroup. */
  startTime: string;
  endTime: string;
  className: string | null;
  teacherName: string | null;
  /** Resolved (effective) room name: explicit entry room → teacher's default room → class homeroom room. */
  roomName: string | null;
  subjectName: string | null;
  subjectColor: string | null;
  assistants: TimetableAssistant[];
  /** An EDP course, not a lesson: it belongs to no class, and the office moves it from the EDP
   *  screens, where the children's afternoons move with it. */
  isEdp: boolean;
};

export type TimetableViewGroup = {
  id: string;
  name: string;
  subtitle: string | null;
  /** Only for the class view: the grade the class teaches, which is what the pickers filter on. */
  gradeLevel?: number;
  cells: TimetableViewCell[];
  /** Class-timed period template — only populated for by==='class' groups. */
  periods?: TimetablePeriod[];
};

export type TimetableView = {
  periods: TimetablePeriod[];
  groups: TimetableViewGroup[];
};

export type TimetableCell = {
  id: string;
  weekday: number;
  periodId: string;
  /** User id of the lead teacher (staff user). */
  teacherId: string | null;
  teacherName: string | null;
  /** Explicit room override stored on the entry — this is what the cell dialog edits. */
  roomId: string | null;
  /** Resolved (effective) room name: explicit entry room → teacher's default room → class homeroom room. */
  roomName: string | null;
  /** Resolved (effective) room id, following the same precedence as `roomName`. */
  resolvedRoomId: string | null;
  subjectId: string | null;
  subjectName: string | null;
  subjectColor: string | null;
  assistants: TimetableAssistant[];
};

export type TimetablePeriod = {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  kind: string;
  sortOrder: number;
  active: boolean;
  /** Days this period runs on: 1=Mon … 6=Sat. The grid greys the cells of days it does not. */
  weekdays: number[];
  /**
   * True only for a period the school has marked as an EDP block — never an ordinary
   * `activity` period like Homeroom, which shares `kind: 'activity'` with it. Never derive this
   * from the period's name: the school can rename a period from the admin.
   */
  isEdp: boolean;
};

export type TimetableClassSummary = {
  id: string;
  schoolYearId: string;
  gradeLevel: number;
  name: string;
  homeroomTeacherId: string | null;
  homeroomTeacherName: string | null;
};

export type TimetableGrid = {
  class: TimetableClassSummary;
  periods: TimetablePeriod[];
  entries: TimetableCell[];
};

// ── A student's own week (lessons plus EDP), as the family sees it ──────────

/** Mirrors `FamilyTimetablePeriodResponse`: no `active`, since only active periods are sent. */
export type FamilyTimetablePeriod = {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  kind: string;
  sortOrder: number;
  weekdays: number[];
  isEdp: boolean;
};

/** Mirrors `FamilyTimetableEntryResponse`: no editing ids, an EDP course marked as one. */
export type FamilyTimetableEntry = {
  weekday: number;
  periodId: string;
  subjectName: string | null;
  subjectColor: string | null;
  teacherName: string | null;
  roomName: string | null;
  isEdp: boolean;
};

/** One meeting of an EDP course that meets on its own dates rather than a weekday. */
export type FamilyTimetableSpecialDate = {
  date: string;
  startsAt: string;
  endsAt: string;
  subjectName: string;
  teacherName: string | null;
};

export type FamilyTimetableClosure = { date: string; label: string | null; scope: string };

/** Mirrors `FamilyTimetableResponse`. `class` is null while the child's timetable is unpublished. */
export type FamilyTimetable = {
  gradeLevel: number | null;
  class: { name: string; gradeLevel: number; homeroomTeacherName: string | null } | null;
  periods: FamilyTimetablePeriod[];
  entries: FamilyTimetableEntry[];
  specialDates: FamilyTimetableSpecialDate[];
  closures: FamilyTimetableClosure[];
};

export type Conflict = {
  classId: string;
  className: string;
  gradeLevel: number;
  kind: 'teacher' | 'room';
  periodId: string;
  weekday: number;
};

export type UpsertCellBody = {
  classId: string;
  weekday: number;
  periodId: string;
  /** Staff user id (validated server-side against admin/teacher/employee roles). */
  teacherId?: string | null;
  roomId?: string | null;
  subjectId?: string | null;
  /** Staff user ids. */
  assistantTeacherIds?: string[];
};

export type ClearCellBody = {
  classId: string;
  weekday: number;
  periodId: string;
};

export type FindConflictsParams = {
  schoolYearId: string;
  weekday: number;
  periodId: string;
  teacherId?: string;
  roomId?: string;
  excludeClassId?: string;
};

export const timetableApi = {
  getGrid: (classId: string) => fetchApi<TimetableGrid>(`${TIMETABLE_PATH}?classId=${encodeURIComponent(classId)}`),

  /** One student's week as the family sees it — lessons plus the EDP rows for the round under way. */
  getStudentTimetable: (studentId: string) =>
    fetchApi<FamilyTimetable>(`${TIMETABLE_PATH}/students/${encodeURIComponent(studentId)}`),

  upsertCell: (body: UpsertCellBody) =>
    fetchApi<TimetableCell>(`${TIMETABLE_PATH}/cell`, { method: 'PUT', body: JSON.stringify(body) }),

  clearCell: (body: ClearCellBody) =>
    fetchApi<{ success: boolean }>(`${TIMETABLE_PATH}/cell`, { method: 'DELETE', body: JSON.stringify(body) }),

  findConflicts: (params: FindConflictsParams) => {
    const qs = new URLSearchParams();
    qs.set('schoolYearId', params.schoolYearId);
    qs.set('weekday', String(params.weekday));
    qs.set('periodId', params.periodId);
    if (params.teacherId) qs.set('teacherId', params.teacherId);
    if (params.roomId) qs.set('roomId', params.roomId);
    if (params.excludeClassId) qs.set('excludeClassId', params.excludeClassId);
    return fetchApi<Conflict[]>(`${TIMETABLE_PATH}/conflicts?${qs.toString()}`);
  },
};

export const timetableViewsApi = {
  // No year named means the active one — the only option for a caller who cannot read the list.
  getViews: (by: TimetableViewBy, schoolYearId?: string, teacherId?: string) => {
    const qs = new URLSearchParams({ by });
    if (schoolYearId) qs.append('schoolYearId', schoolYearId);
    if (teacherId) qs.append('teacherId', teacherId);
    return fetchApi<TimetableView>(`${TIMETABLE_PATH}/views?${qs.toString()}`);
  },
};
